import React, { useMemo, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const AMBER = '#FAAB1C', BLUE = '#21A84A';
const fmt = (n) => '$' + Math.round(n).toLocaleString();

function model(bill, escalator = 3.0) {
  const kwh = (bill * 12) / 0.24;
  const kw = Math.min(16, Math.max(4, kwh / 1250));
  const gross = kw * 1000 * 3.25;
  const net = gross * 0.7;
  const r = 0.0599 / 12, n = 300;
  const pmt = (net * r) / (1 - Math.pow(1 + r, -n));
  const rows = []; let cu = 0, cs = 0;
  for (let y = 1; y <= 25; y++) {
    const util = bill * 12 * Math.pow(1 + escalator / 100, y - 1);
    const solar = pmt * 12 + 10 * 12;
    cu += util; cs += solar;
    rows.push({ year: `Y${y}`, y, utility: Math.round(cu), solar: Math.round(cs) });
  }
  return { kw, gross, net, pmt, rows, savings: cu - cs, monthlyAfter: pmt + 10 };
}

export default function SavingsVisualizer() {
  const [bill, setBill] = useState(240);
  const m = useMemo(() => model(bill), [bill]);
  return (
    <div className="grid lg:grid-cols-[1fr_1.3fr] gap-8 lg:gap-12 items-center" data-testid="savings-visualizer">
      <div>
        <p className="text-[11px] font-bold tracking-[0.25em]" style={{ color: BLUE }}>25-YEAR MONEY MAP</p>
        <h2 className="font-heading font-black text-4xl md:text-5xl mt-3 tracking-tight leading-[1.02] text-slate-900">Doing nothing is the most expensive option.</h2>
        <p className="text-slate-500 mt-5 text-lg">Utilities in the Northeast raise rates ~3% a year, forever. Solar locks your cost on day one. Slide your bill and watch the gap open.</p>
        <div className="mt-8 rounded-3xl p-6 text-white" style={{ background: '#0C2417' }}>
          <div className="flex items-end justify-between">
            <div><p className="text-[10px] font-bold tracking-widest text-slate-400">YOUR MONTHLY ELECTRIC BILL</p><p className="font-heading font-black text-4xl mt-1" data-testid="savings-bill">${bill}</p></div>
            <div className="text-right"><p className="text-[10px] font-bold tracking-widest text-slate-400">WITH SAMARITAN</p><p className="font-heading font-black text-4xl mt-1" style={{ color: AMBER }}>{fmt(m.monthlyAfter)}<span className="text-sm text-slate-400">/mo</span></p></div>
          </div>
          <input type="range" min="80" max="800" step="10" value={bill} onChange={e => setBill(Number(e.target.value))} className="w-full mt-5 accent-[#FAAB1C]" data-testid="savings-slider" aria-label="Monthly bill" />
          <div className="grid grid-cols-3 gap-3 mt-5">
            {[[`${m.kw.toFixed(1)} kW`, 'system size'], [fmt(m.gross * 0.3), '30% federal credit'], [fmt(m.savings), '25-yr savings']].map(([v, l]) => (
              <div key={l} className="rounded-xl p-3 border border-white/10 bg-white/[0.04]"><p className="font-heading font-black text-lg" style={{ color: l.includes('savings') ? '#4ADE80' : 'white' }}>{v}</p><p className="text-[9px] font-bold tracking-widest text-slate-400 mt-1 uppercase">{l}</p></div>
            ))}
          </div>
        </div>
      </div>
      <div className="rounded-3xl p-5 sm:p-7 border border-slate-200 bg-white shadow-[0_30px_60px_-30px_rgba(12,36,23,0.35)]">
        <div className="flex items-center justify-between mb-4">
          <p className="text-[10px] font-bold tracking-widest text-slate-400">CUMULATIVE COST · 25 YEARS</p>
          <div className="flex gap-4 text-[10px] font-bold tracking-widest"><span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#EF4444' }} />UTILITY</span><span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: AMBER }} />SAMARITAN SOLAR</span></div>
        </div>
        <div className="h-72 sm:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={m.rows} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="gU" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#EF4444" stopOpacity={0.35} /><stop offset="100%" stopColor="#EF4444" stopOpacity={0} /></linearGradient>
                <linearGradient id="gS" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={AMBER} stopOpacity={0.45} /><stop offset="100%" stopColor={AMBER} stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid stroke="#EEF2F7" vertical={false} />
              <XAxis dataKey="year" tick={{ fontSize: 10, fill: '#94A3B8' }} interval={4} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} tickFormatter={v => `$${Math.round(v / 1000)}k`} axisLine={false} tickLine={false} width={44} />
              <Tooltip formatter={(v, n) => [fmt(v), n === 'utility' ? 'Utility' : 'Samaritan Solar']} contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', fontSize: 12 }} />
              <Area type="monotone" dataKey="utility" stroke="#EF4444" strokeWidth={2.5} fill="url(#gU)" isAnimationActive animationDuration={900} />
              <Area type="monotone" dataKey="solar" stroke={AMBER} strokeWidth={2.5} fill="url(#gS)" isAnimationActive animationDuration={900} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <p className="text-xs text-slate-400 mt-3">Illustrative: 3% utility escalator, $3.25/W installed, 30% ITC, 5.99% APR 25-yr loan, $10/mo grid connection. Your proposal uses your real roof, your rate, and your utility.</p>
      </div>
    </div>
  );
}
